import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, ShieldCheck, Truck, RotateCw, Check, ArrowLeft } from "lucide-react";
import { fetchProduct } from "../lib/api";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImage, setActiveImage] = useState(0);
  const { add } = useCart();

  useEffect(() => {
    fetchProduct(slug).then(setProduct).catch(() => setProduct(null));
  }, [slug]);

  // Inject Product JSON-LD for SEO
  useEffect(() => {
    if (!product) return;
    const ld = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      description: product.description,
      image: [product.image],
      brand: { "@type": "Brand", name: "BananaX" },
      category: product.category_name,
      sku: product.id,
      offers: {
        "@type": "Offer",
        url: `https://bananax.in/product/${product.slug}`,
        priceCurrency: product.currency || "INR",
        price: product.price,
        availability: "https://schema.org/InStock",
        itemCondition: "https://schema.org/NewCondition",
      },
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.9",
        reviewCount: "1240",
      },
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "product-ld";
    script.textContent = JSON.stringify(ld);
    // remove any previous
    document.getElementById("product-ld")?.remove();
    document.head.appendChild(script);

    // Update document title + meta description for SEO
    const prevTitle = document.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute("content");
    document.title = `${product.name} · BananaX — Premium Condoms`;
    metaDesc?.setAttribute("content", product.description.slice(0, 158));

    return () => {
      document.getElementById("product-ld")?.remove();
      document.title = prevTitle;
      if (prevDesc) metaDesc?.setAttribute("content", prevDesc);
    };
  }, [product]);

  if (!product) {
    return (
      <div className="pt-40 text-center min-h-screen" data-testid="product-loading">
        <p className="text-mist">Loading…</p>
      </div>
    );
  }

  const accent = product.accent || "#D4AF37";

  const onAdd = () => {
    add(product, qty);
    toast.success(`${qty} × ${product.name} added`);
  };

  return (
    <div className="pt-32 pb-24 min-h-screen" data-testid="product-detail-page">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <Link to="/shop" className="inline-flex items-center gap-2 text-mist hover:text-gold text-xs tracking-[0.22em] uppercase mb-10">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to shop
        </Link>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 space-y-4"
          >
            <div className="relative product-image-wrap aspect-[4/5] border hairline-gold overflow-hidden">
              <div
                className="absolute inset-0 opacity-50 pointer-events-none"
                style={{ background: `radial-gradient(ellipse at center, ${accent}33 0%, transparent 70%)` }}
              ></div>
              <img
                src={activeImage === 0 ? product.image : product.image_back}
                alt={product.name}
                className="relative w-full h-full object-cover"
                data-testid="product-main-image"
              />
            </div>
            {product.image_back && (
              <div className="grid grid-cols-2 gap-3" data-testid="product-thumbnails">
                {[product.image, product.image_back].map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImage(i)}
                    className={`aspect-[4/5] border overflow-hidden transition-all ${
                      activeImage === i ? "border-gold" : "border-white/10 hover:border-white/40"
                    }`}
                    data-testid={`product-thumbnail-${i}`}
                  >
                    <img src={src} alt={`view ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5 space-y-6 lg:sticky lg:top-32 self-start"
          >
            <div className="label-eyebrow" style={{ color: accent }}>{product.category_name}</div>
            <h1 className="font-serif text-5xl md:text-6xl tracking-tight leading-none">
              {product.name}
            </h1>
            {product.sub_tagline && (
              <div className="flex items-center gap-2" data-testid="product-sub-tagline">
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full"
                  style={{ background: accent, boxShadow: `0 0 12px ${accent}` }}
                ></span>
                <span className="text-[11px] tracking-[0.3em] uppercase font-medium" style={{ color: accent }}>
                  {product.sub_tagline}
                </span>
              </div>
            )}
            <p className="font-serif text-2xl text-gold italic" data-testid="product-tagline">{product.tagline}</p>
            <div className="flex items-baseline gap-4 flex-wrap">
              <span className="num-display text-4xl text-bone" data-testid="product-price">₹{product.price}</span>
              {product.mrp && product.mrp > product.price && (
                <>
                  <span className="num-display text-xl text-mist/50 line-through" data-testid="product-mrp">₹{product.mrp}</span>
                  <span
                    className="num-display text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-400/30 px-2 py-1 tracking-wider"
                    data-testid="product-discount"
                  >
                    Save ₹{product.mrp - product.price} · {Math.round((1 - product.price / product.mrp) * 100)}% OFF
                  </span>
                </>
              )}
            </div>
            <p className="text-mist font-light leading-relaxed text-lg">{product.description}</p>

            <div className="border-y border-white/10 py-6 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="label-eyebrow text-mist">Size</span>
                <span className="text-bone font-light">{product.size}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="label-eyebrow text-mist">Quantity per box</span>
                <span className="text-bone font-light">{product.quantity} pcs</span>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center border border-white/15">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-11 h-11 text-bone hover:text-gold" data-testid="qty-decrease-btn">
                  <Minus className="w-4 h-4 mx-auto" />
                </button>
                <span className="w-12 text-center font-serif text-lg text-bone" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="w-11 h-11 text-bone hover:text-gold" data-testid="qty-increase-btn">
                  <Plus className="w-4 h-4 mx-auto" />
                </button>
              </div>
              <button onClick={onAdd} className="btn-primary flex-1" data-testid="add-to-cart-btn">
                Add to cart
              </button>
            </div>

            <ul className="grid grid-cols-1 gap-3 pt-4">
              {product.features?.map((f) => (
                <li key={f} className="flex items-start gap-3 text-bone/90 font-light text-sm">
                  <Check className="w-4 h-4 text-gold stroke-[1.5] mt-0.5 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-white/10">
              <div className="text-center">
                <ShieldCheck className="w-5 h-5 text-gold stroke-[1.4] mx-auto mb-2" />
                <p className="text-[10px] tracking-[0.2em] uppercase text-mist">100% Tested</p>
              </div>
              <div className="text-center">
                <Truck className="w-5 h-5 text-gold stroke-[1.4] mx-auto mb-2" />
                <p className="text-[10px] tracking-[0.2em] uppercase text-mist">Discreet Ship</p>
              </div>
              <div className="text-center">
                <RotateCw className="w-5 h-5 text-gold stroke-[1.4] mx-auto mb-2" />
                <p className="text-[10px] tracking-[0.2em] uppercase text-mist">Easy returns</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
